import * as React from 'react';

import {Checkbox} from 'shells/checkbox/checkbox';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {TwoPaneModalForm} from 'shells/modal/two-pane-modal-form';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {LicenseType} from 'nutshell-graphql-types';
import type {UserCreateInput} from 'nutshell-graphql-types';

// @ts-expect-error
import {useGetLicenseOverview} from '../../licensing/graphql/hooks';
import {LicenseSwitchList} from '../../licensing/license-switch-list';

import {useGetRoles} from '../roles-permissions/graphql/hooks';
import {RolePicker} from '../roles-permissions/role-picker';

import styles from './create-user-modal.module.css';

type Props = {
    onClose: () => void;
    onSubmit: (arg1: UserCreateInput) => void;
    isSaving: boolean;
    errorMessage?: string | null | undefined;
    existingUserId?: string | null | undefined;
};

export function CreateUserModal(props: Props) {
    const [firstName, setFirstName] = React.useState<string | null | undefined>(null);
    const [lastName, setLastName] = React.useState<string | null | undefined>(null);
    const [email, setEmail] = React.useState<string | null | undefined>(null);
    const [submittedEmail, setSubmittedEmail] = React.useState<string | null | undefined>(null);
    const [roleId, setRoleId] = React.useState<string | null | undefined>(null);
    const [sendInvite, setSendInvite] = React.useState<boolean>(false);
    const [isCrmOn, setIsCrmOn] = React.useState<boolean>(false);
    const [isMarketingOn, setIsMarketingOn] = React.useState<boolean>(false);
    const [isEngagementOn, setIsEngagementOn] = React.useState<boolean>(false);

    const {roles} = useGetRoles();
    const {licenseOverview, isLoading} = useGetLicenseOverview();

    React.useEffect(() => {
        if (licenseOverview) {
            setIsCrmOn(licenseOverview.instanceLicenses[0].canAdd);
            setIsMarketingOn(licenseOverview.instanceLicenses[1].canAdd);
            setIsEngagementOn(licenseOverview.instanceLicenses[2].canAdd);
        }
    }, [licenseOverview]);

    React.useEffect(() => {
        if (roles && !roleId) {
            const defaultRole = roles.find((role) => role.isDefault);
            if (defaultRole) {
                setRoleId(defaultRole.id);
            }
        }
    }, [roles, roleId]);

    const handleSubmit = () => {
        if (firstName && lastName && roleId && email) {
            props.onSubmit({
                firstName,
                lastName,
                email,
                roleId,
                sendInvite,
                licenses: getEnabledLicenses(),
            });
        }
    };

    const handleSwitchToggle = (licenseType: LicenseType) => {
        // Optimistically update the state and handle error checking in the
        // backend when we try to create the user
        if (licenseType === 'CRM') {
            setIsCrmOn(!isCrmOn);
        } else if (licenseType === 'MARKETING') {
            setIsMarketingOn(!isMarketingOn);
        } else if (licenseType === 'ENGAGEMENT') {
            setIsEngagementOn(!isEngagementOn);
        }
    };

    const getEnabledLicenses = () => {
        // @ts-expect-error
        const licenses = [];

        if (licenseOverview) {
            // @ts-expect-error
            licenseOverview.instanceLicenses.forEach((license) => {
                if (getIsOnStateForLicenseType(license.licenseType)) {
                    licenses.push(license.licenseType);
                }
            });
        }

        // @ts-expect-error
        return licenses;
    };

    const getIsOnStateForLicenseType = (licenseType: LicenseType) => {
        switch (licenseType) {
            case 'CRM':
                return isCrmOn;
            case 'MARKETING':
                return isMarketingOn;
            case 'ENGAGEMENT':
                return isEngagementOn;
            default:
                return false;
        }
    };

    const handleUserAlreadyExists = (existingUserId: string) => {
        return (
            <>
                {submittedEmail} is already a user. Edit their access{' '}
                <Link
                    variant='primary'
                    href={`/setup/user/settings/${existingUserId}`}
                    onClick={props.onClose}
                >
                    here
                </Link>
                .
            </>
        );
    };

    return (
        <TwoPaneModalForm
            onSubmit={() => {
                setSubmittedEmail(email);
                handleSubmit();
            }}
            onClose={props.onClose}
            submitButtonText={props.isSaving ? 'Creating…' : 'Create user'}
            isSaving={props.isSaving}
            saveButtonDisabled={!firstName || !lastName || !roleId || !email}
            // @ts-expect-error
            isOpen={true}
            maxHeight={600}
            minHeight={600}
            maxWidth={900}
            headerText='Add a new user'
            errorMessage={props.errorMessage}
            leftSection={
                <StackLegacy spacing={8}>
                    <UppercaseLabel>Information</UppercaseLabel>
                    <StackLegacy spacing={24}>
                        <StackLegacy spacing={4}>
                            <Body>First name</Body>
                            <div>
                                <TextField
                                    id='firstName'
                                    // @ts-expect-error
                                    name='firstName'
                                    maxLength={255}
                                    focusOnMount={true}
                                    selectOnFocus={true}
                                    required={true}
                                    value={firstName || ''}
                                    onChange={(name: string) => setFirstName(name)}
                                />
                            </div>
                        </StackLegacy>
                        <StackLegacy spacing={4}>
                            <Body>Last name</Body>
                            <div>
                                <TextField
                                    id='lastName'
                                    // @ts-expect-error
                                    name='lastName'
                                    maxLength={255}
                                    selectOnFocus={true}
                                    required={true}
                                    value={lastName || ''}
                                    onChange={(name: string) => setLastName(name)}
                                />
                            </div>
                        </StackLegacy>
                        <StackLegacy spacing={4}>
                            <Body>Email</Body>
                            <div>
                                <TextField
                                    id='email'
                                    // @ts-expect-error
                                    name='email'
                                    maxLength={255}
                                    selectOnFocus={true}
                                    required={true}
                                    value={email || ''}
                                    onChange={(name: string) => setEmail(name)}
                                />
                            </div>
                        </StackLegacy>
                        <Checkbox
                            label='Send an email invitation to join your team'
                            isLarge={true}
                            checked={sendInvite}
                            onChange={() => setSendInvite(!sendInvite)}
                        />
                        <StackLegacy spacing={4}>
                            <div className='flex gap-2'>
                                <Body>Role</Body>
                                <PopoverInfo
                                    info='Change the user’s role to adjust their access and permissions.'
                                    bodyColor='dark'
                                />
                            </div>
                            <RolePicker
                                onSelectRole={(id: string) => setRoleId(id)}
                                selectedRoleId={roleId}
                                roles={roles || []}
                                // @ts-expect-error
                                autoFocus={false}
                            />
                        </StackLegacy>
                    </StackLegacy>
                </StackLegacy>
            }
            rightSection={
                <StackLegacy spacing={8}>
                    <UppercaseLabel>Licensing</UppercaseLabel>
                    <LicenseSwitchList
                        isLoading={isLoading}
                        licenseOverview={licenseOverview}
                        handleSwitchToggle={handleSwitchToggle}
                        getIsOnStateForLicenseType={getIsOnStateForLicenseType}
                    />
                </StackLegacy>
            }
            customErrorComponent={
                <div className={styles['error-message']}>
                    <Body color='error'>
                        {props.existingUserId
                            ? handleUserAlreadyExists(props.existingUserId)
                            : props.errorMessage}
                    </Body>
                </div>
            }
        />
    );
}
